/**
 * Keybinding system — data model, types, and default bindings.
 * Modeled after the original Synth Riders editor Controller.cs.
 *
 * Each KeyBinding has a unique `id`, a human-readable `description`,
 * a `category` for grouping in the settings UI, two binding slots
 * (primary + secondary), and a cheatsheet toggle.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type Modifier = 'ctrl' | 'alt' | 'shift';

export interface KeyCombo {
  key: string;       // e.g. 'Mouse0', 'Space', '1', 'e', 'Delete', 'None'
  mods: Modifier[];  // e.g. ['ctrl'], ['alt'], ['ctrl','alt']
}

export interface KeyBinding {
  id: string;
  description: string;
  category: KeyBindingCategory;
  primary: KeyCombo;
  secondary: KeyCombo;
  includeInCheatsheet: boolean;
}

export type KeyBindingCategory =
  | 'input_modifiers'
  | 'map_editing'
  | 'timeline'
  | 'interface'
  | 'camera'
  | 'volume';

export const CATEGORY_LABELS: Record<KeyBindingCategory, string> = {
  input_modifiers: 'Input Modifiers',
  map_editing: 'Map Editing Controls',
  timeline: 'Timeline Controls',
  interface: 'Interface Controls',
  camera: 'Camera Controls',
  volume: 'Volume Controls',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const k = (key: string, ...mods: Modifier[]): KeyCombo => ({ key, mods });
const none: KeyCombo = { key: 'None', mods: [] };

// ── Default Keybindings ──────────────────────────────────────────────────────

export const DEFAULT_KEYBINDINGS: KeyBinding[] = [
  // ── Input Modifiers ────────────────────────────────────────────
  {
    id: 'mod1',
    description: 'Input Modifier 1',
    category: 'input_modifiers',
    primary: k('LeftControl'),
    secondary: k('JoystickButton0'),
    includeInCheatsheet: false,
  },
  {
    id: 'mod2',
    description: 'Input Modifier 2',
    category: 'input_modifiers',
    primary: k('LeftAlt'),
    secondary: k('JoystickButton1'),
    includeInCheatsheet: false,
  },
  {
    id: 'mod3',
    description: 'Input Modifier 3',
    category: 'input_modifiers',
    primary: k('LeftShift'),
    secondary: k('JoystickButton2'),
    includeInCheatsheet: false,
  },

  // ── Map Editing Controls ───────────────────────────────────────
  {
    id: 'place_note',
    description: 'Place a Note/Wall on the Grid',
    category: 'map_editing',
    primary: k('Mouse0'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'drag_snap_object',
    description: 'Drag Note/Wall or Snap Note/Wall to Grid',
    category: 'map_editing',
    primary: k('Mouse0', 'alt'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'snap_grid_to_object',
    description: 'Snap Grid to Note/Wall',
    category: 'map_editing',
    primary: k('Mouse1', 'alt'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'rotate_wall_cw',
    description: 'Rotate Wall Clockwise (During Drag)',
    category: 'map_editing',
    primary: k('e', 'alt'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'rotate_wall_ccw',
    description: 'Rotate Wall Counter-Clockwise (During Drag)',
    category: 'map_editing',
    primary: k('q', 'alt'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'remote_delete',
    description: 'Remote Delete Note/Wall',
    category: 'map_editing',
    primary: k('Mouse1', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'toggle_play',
    description: 'Toggle Play',
    category: 'map_editing',
    primary: k('Space'),
    secondary: k('JoystickButton3'),
    includeInCheatsheet: false,
  },
  {
    id: 'toggle_play_return',
    description: 'Toggle Play (Return)',
    category: 'map_editing',
    primary: k('r'),
    secondary: k('Space', 'ctrl'),
    includeInCheatsheet: false,
  },
  {
    id: 'select_left_hand',
    description: 'Select Left Hand Note',
    category: 'map_editing',
    primary: k('1'),
    secondary: k('Numpad1'),
    includeInCheatsheet: false,
  },
  {
    id: 'select_right_hand',
    description: 'Select Right Hand Note',
    category: 'map_editing',
    primary: k('2'),
    secondary: k('Numpad2'),
    includeInCheatsheet: false,
  },
  {
    id: 'select_onehand_special',
    description: 'Select One Hand Special Note',
    category: 'map_editing',
    primary: k('3'),
    secondary: k('Numpad3'),
    includeInCheatsheet: false,
  },
  {
    id: 'select_twohand_special',
    description: 'Select Two Hand Special Note',
    category: 'map_editing',
    primary: k('4'),
    secondary: k('Numpad4'),
    includeInCheatsheet: false,
  },
  {
    id: 'add_node_to_rail',
    description: 'Add Node to Nearest Existing Rail',
    category: 'map_editing',
    primary: k('Mouse0', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'toggle_long_note',
    description: 'Toggle Long Note Mode',
    category: 'map_editing',
    primary: k('l'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'toggle_bookmark',
    description: 'Toggle Bookmark at Current Time',
    category: 'map_editing',
    primary: k('9'),
    secondary: k('Numpad9'),
    includeInCheatsheet: false,
  },
  {
    id: 'clear_bookmarks',
    description: 'Clear All Bookmarks',
    category: 'map_editing',
    primary: k('F8'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'toggle_flash',
    description: 'Toggle Flash Effect at Current Time',
    category: 'map_editing',
    primary: k('9', 'ctrl'),
    secondary: k('Numpad9', 'ctrl'),
    includeInCheatsheet: false,
  },
  {
    id: 'area_selection',
    description: 'Area Selection',
    category: 'map_editing',
    primary: k('z'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'select_all',
    description: 'Select All',
    category: 'map_editing',
    primary: k('a', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'delete_selected',
    description: 'Delete Current/Selected Objects',
    category: 'map_editing',
    primary: k('Delete'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'delete_all',
    description: 'Delete ALL Objects (current difficulty)',
    category: 'map_editing',
    primary: k('Delete', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'copy',
    description: 'Copy the Current/Selected Objects',
    category: 'map_editing',
    primary: k('c', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'cut',
    description: 'Cut the Current/Selected Objects',
    category: 'map_editing',
    primary: k('x', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'paste',
    description: 'Paste the Copied Objects',
    category: 'map_editing',
    primary: k('v', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'paste_mirror',
    description: 'Mirror Paste the Copied Objects',
    category: 'map_editing',
    // Ctrl+Shift+V, not Alt+V: on Windows, Alt+letter opens the browser's menu bar,
    // so the page never received the keystroke.
    primary: k('v', 'ctrl', 'shift'),
    secondary: k('v', 'alt'),
    includeInCheatsheet: true,
  },
  {
    id: 'undo',
    description: 'Undo Previous Action',
    category: 'map_editing',
    primary: k('z', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'redo',
    description: 'Redo Undone Action',
    category: 'map_editing',
    primary: k('y', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'flip_object',
    description: 'Flip Object Handedness and Position Over X Axis',
    category: 'map_editing',
    primary: k('f'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'flip_object_y',
    description: 'Flip Position Over Y Axis',
    category: 'map_editing',
    primary: k('f', 'alt'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'duplicate_opposite',
    description: 'Duplicate Note/Rail to Opposite Hand',
    category: 'map_editing',
    primary: k('d', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'scale_selection_expand',
    description: 'Scale Selection — Expand Spacing (×2)',
    category: 'map_editing',
    primary: k('k', 'shift'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'scale_selection_compress',
    description: 'Scale Selection — Compress Spacing (÷2)',
    category: 'map_editing',
    primary: k('k', 'ctrl', 'shift'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'toggle_mirror_mode',
    description: 'Toggle Mirror Mode (mirror everything you place)',
    category: 'map_editing',
    primary: k('F3'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'mirror_invert_y',
    description: 'Invert Y Axis in Mirror Mode',
    category: 'map_editing',
    primary: k('y'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'join_notes_rail',
    description: 'Join Selected Notes Into a Rail',
    category: 'map_editing',
    primary: k('j'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'split_rail',
    description: 'Split Rail at Selected Node',
    category: 'map_editing',
    primary: k('s', 'alt'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'recalc_rail',
    description: 'Recalc Rail Nodes by Step',
    category: 'map_editing',
    primary: k('n', 'alt'),
    secondary: none,
    includeInCheatsheet: true,
  },
  // Wall/obstacle placement hotkeys. Legacy uses Ctrl+1..8, but Chrome hard-
  // reserves Ctrl+1..8 for tab switching (a page can't preventDefault them), so
  // in the web editor they'd never fire. Use Alt+1..8 instead (not reserved).
  {
    id: 'wall_left', description: 'Place Left Wall at Playhead',
    category: 'map_editing', primary: k('1', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'wall_right', description: 'Place Right Wall at Playhead',
    category: 'map_editing', primary: k('2', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'wall_center', description: 'Place Center Wall at Playhead',
    category: 'map_editing', primary: k('3', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'wall_diag_left', description: 'Place Left Diagonal Wall at Playhead',
    category: 'map_editing', primary: k('4', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'wall_diag_right', description: 'Place Right Diagonal Wall at Playhead',
    category: 'map_editing', primary: k('5', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'wall_crouch', description: 'Place Crouch Wall at Playhead',
    category: 'map_editing', primary: k('6', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'wall_square', description: 'Place Square Obstacle at Playhead',
    category: 'map_editing', primary: k('7', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'wall_triangle', description: 'Place Triangle Obstacle at Playhead',
    category: 'map_editing', primary: k('8', 'alt'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'add_note_playing', description: 'Add Note at Playhead (while playing)',
    category: 'map_editing', primary: k('n'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'toggle_same_note_overwrite', description: 'Toggle Same-Note Overwrite Mode',
    category: 'map_editing', primary: k('w', 'shift'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'snip_rail', description: 'Snip Rail — Insert Node at Playhead',
    category: 'map_editing', primary: k('s', 'shift'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'cycle_note_type', description: 'Cycle Selected Note Type (hand)',
    category: 'map_editing', primary: k('t'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'snap_selection_to_rail', description: 'Snap Selection to Nearest Rail',
    category: 'map_editing', primary: k('r', 'shift'), secondary: none, includeInCheatsheet: true,
  },
  {
    id: 'save_map',
    description: 'Save the Map',
    category: 'map_editing',
    primary: k('s', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'select_tool',
    description: 'Select Tool Mode',
    category: 'map_editing',
    primary: k('v'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'eraser_tool',
    description: 'Eraser Tool Mode',
    category: 'map_editing',
    primary: k('e'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'change_note_color',
    description: 'Change Existing Note Color',
    category: 'map_editing',
    primary: k('Mouse0', 'shift'),
    secondary: none,
    includeInCheatsheet: true,
  },

  // ── Timeline Controls ──────────────────────────────────────────
  {
    id: 'move_backward',
    description: 'Move Backward on Timeline',
    category: 'timeline',
    primary: k('ArrowDown'),
    secondary: k('s'),
    includeInCheatsheet: false,
  },
  {
    id: 'move_forward',
    description: 'Move Forward on Timeline',
    category: 'timeline',
    primary: k('ArrowUp'),
    secondary: k('w'),
    includeInCheatsheet: false,
  },
  {
    id: 'jump_next_bookmark',
    description: 'Jump to Next Bookmark',
    category: 'timeline',
    primary: k(']'),
    secondary: k('PageUp'),
    includeInCheatsheet: false,
  },
  {
    id: 'jump_prev_bookmark',
    description: 'Jump to Previous Bookmark',
    category: 'timeline',
    primary: k('['),
    secondary: k('PageDown'),
    includeInCheatsheet: false,
  },
  {
    id: 'timeline_start',
    description: 'Navigate to Timeline Start',
    category: 'timeline',
    primary: k('Home'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'timeline_end',
    description: 'Navigate to Timeline End',
    category: 'timeline',
    primary: k('End'),
    secondary: none,
    includeInCheatsheet: false,
  },

  // ── Interface Controls ─────────────────────────────────────────
  // A / D step the snap grid (desktop-editor parity, and reachable when the arrow
  // keys are busy nudging a selection — App.tsx claims the arrows in capture phase
  // whenever notes are selected). Reviewer-confirmed direction: D = finer
  // subdivision (increase the division), A = coarser (closer to a whole step).
  {
    id: 'beat_step_down',
    description: 'Finer Beat Step (increase the division)',
    category: 'interface',
    primary: k('d'),
    secondary: k('ArrowLeft'),
    includeInCheatsheet: true,
  },
  {
    id: 'beat_step_up',
    description: 'Coarser Beat Step (toward a whole beat)',
    category: 'interface',
    primary: k('a'),
    secondary: k('ArrowRight'),
    includeInCheatsheet: true,
  },
  {
    id: 'cycle_step_type',
    description: 'Cycle Step Type (Straight / Triplet / All)',
    category: 'interface',
    primary: k('Tab'),
    secondary: none,
    includeInCheatsheet: true,
  },
  // Shift+Arrow, not bare Arrow: these used to duplicate beat_step_down/up's
  // bindings, and findMatchingAction returns the FIRST match — so playback speed
  // was permanently unreachable.
  {
    id: 'playback_speed_down',
    description: 'Adjust Playback Speed Down During Play',
    category: 'interface',
    primary: k('ArrowLeft', 'shift'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'playback_speed_up',
    description: 'Adjust Playback Speed Up During Play',
    category: 'interface',
    primary: k('ArrowRight', 'shift'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'decrease_music_vol',
    description: 'Decrease Music Volume',
    category: 'interface',
    primary: k('-'),
    secondary: k('NumpadSubtract'),
    includeInCheatsheet: false,
  },
  {
    id: 'increase_music_vol',
    description: 'Increase Music Volume',
    category: 'interface',
    primary: k('='),
    secondary: k('NumpadAdd'),
    includeInCheatsheet: false,
  },
  {
    id: 'decrease_sfx_vol',
    description: 'Decrease SFX Volume',
    category: 'interface',
    primary: k('-', 'ctrl'),
    secondary: k('NumpadSubtract', 'ctrl'),
    includeInCheatsheet: false,
  },
  {
    id: 'increase_sfx_vol',
    description: 'Increase SFX Volume',
    category: 'interface',
    primary: k('=', 'ctrl'),
    secondary: k('NumpadAdd', 'ctrl'),
    includeInCheatsheet: false,
  },
  {
    id: 'toggle_grid_guide',
    description: 'Toggle the Grid Guide',
    category: 'interface',
    primary: k('g'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'cycle_grid_guide',
    description: 'Cycle the Grid Guide Type',
    category: 'interface',
    primary: k('g', 'ctrl'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'toggle_snap_to_grid',
    description: 'Toggle Snap to Grid',
    category: 'interface',
    primary: k('F3', 'ctrl'),
    secondary: none,
    includeInCheatsheet: true,
  },
  {
    id: 'toggle_sidebars',
    description: 'Toggle Sidebars',
    category: 'interface',
    primary: k('Tab', 'ctrl'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'toggle_keybindings_panel',
    description: 'Toggle the Keybindings Window',
    category: 'interface',
    primary: k('F1'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'open_settings',
    description: 'Open Settings',
    category: 'interface',
    primary: k(',', 'ctrl'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'open_file_manager',
    description: 'Open File Manager',
    category: 'interface',
    primary: k('o', 'ctrl'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'new_map',
    description: 'New Map',
    category: 'interface',
    primary: k('n', 'ctrl'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'toggle_metronome',
    description: 'Toggle the Metronome',
    category: 'interface',
    primary: k('m', 'ctrl'),
    secondary: none,
    includeInCheatsheet: false,
  },

  // ── Camera Controls ────────────────────────────────────────────
  {
    id: 'camera_center',
    description: 'Enable the Center View Camera',
    category: 'camera',
    primary: k('5'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'camera_left',
    description: 'Enable the Left View Camera',
    category: 'camera',
    primary: k('6'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'camera_right',
    description: 'Enable the Right View Camera',
    category: 'camera',
    primary: k('7'),
    secondary: none,
    includeInCheatsheet: false,
  },
  {
    id: 'camera_free',
    description: 'Enable the Free View Camera',
    category: 'camera',
    primary: k('8'),
    secondary: none,
    includeInCheatsheet: false,
  },
];

// ── Utilities ────────────────────────────────────────────────────────────────

/**
 * Format a KeyCombo for display (e.g. "Ctrl+E", "Mouse0", "LeftAlt", etc.)
 */
export function formatCombo(combo: KeyCombo): string {
  if (combo.key === 'None') return 'None';
  const parts: string[] = [];
  if (combo.mods.includes('ctrl'))  parts.push('Ctrl');
  if (combo.mods.includes('alt'))   parts.push('Alt');
  if (combo.mods.includes('shift')) parts.push('Shift');
  parts.push(formatKeyName(combo.key));
  return parts.join('+');
}

/** Pretty-print a key name for display */
export function formatKeyName(key: string): string {
  const map: Record<string, string> = {
    'ArrowUp': 'UpArrow', 'ArrowDown': 'DownArrow',
    'ArrowLeft': 'LeftArrow', 'ArrowRight': 'RightArrow',
    ' ': 'Space', 'Mouse0': 'Mouse0', 'Mouse1': 'Mouse1', 'Mouse2': 'Mouse2',
    'NumpadAdd': 'KeypadPlus', 'NumpadSubtract': 'KeypadMinus',
    'Numpad0': 'Keypad0', 'Numpad1': 'Keypad1', 'Numpad2': 'Keypad2',
    'Numpad3': 'Keypad3', 'Numpad4': 'Keypad4', 'Numpad5': 'Keypad5',
    'Numpad6': 'Keypad6', 'Numpad7': 'Keypad7', 'Numpad8': 'Keypad8',
    'Numpad9': 'Keypad9',
    '-': 'Minus', '=': 'Equals', '[': 'LeftBracket', ']': 'RightBracket',
    ',': 'Comma',
  };
  return map[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Convert a DOM KeyboardEvent to our normalized key string.
 * We use `event.key` (lowercased for letters) for most keys,
 * with special cases for arrows, numpad, etc.
 */
export function eventToKey(e: KeyboardEvent): string {
  // Use event.code for numpad keys to distinguish from regular numbers
  if (e.code.startsWith('Numpad')) return e.code;
  // Function keys
  if (e.key.startsWith('F') && e.key.length > 1) return e.key;
  // Special keys
  const specials: Record<string, string> = {
    'ArrowUp': 'ArrowUp', 'ArrowDown': 'ArrowDown',
    'ArrowLeft': 'ArrowLeft', 'ArrowRight': 'ArrowRight',
    'Delete': 'Delete', 'Backspace': 'Backspace',
    'Home': 'Home', 'End': 'End',
    'PageUp': 'PageUp', 'PageDown': 'PageDown',
    'Tab': 'Tab', 'Escape': 'Escape', 'Enter': 'Enter',
    ' ': 'Space',
  };
  if (specials[e.key]) return specials[e.key];
  // Regular printable keys → lowercase
  return e.key.toLowerCase();
}

/** Extract active modifiers from a DOM KeyboardEvent */
export function eventToMods(e: KeyboardEvent | MouseEvent): Modifier[] {
  const mods: Modifier[] = [];
  if (e.ctrlKey || e.metaKey) mods.push('ctrl');
  if (e.altKey) mods.push('alt');
  if (e.shiftKey) mods.push('shift');
  return mods;
}

/** Check if two modifier arrays match (order-independent) */
export function modsMatch(a: Modifier[], b: Modifier[]): boolean {
  if (a.length !== b.length) return false;
  const sorted = (arr: Modifier[]) => [...arr].sort().join(',');
  return sorted(a) === sorted(b);
}

/** Check if a KeyCombo matches a key + modifiers */
export function comboMatches(combo: KeyCombo, key: string, mods: Modifier[]): boolean {
  if (combo.key === 'None') return false;
  return combo.key === key && modsMatch(combo.mods, mods);
}

/**
 * Find which keybinding action matches the given key + modifiers.
 * Returns the binding ID or null.
 */
export function findMatchingAction(
  bindings: KeyBinding[],
  key: string,
  mods: Modifier[],
): string | null {
  for (const b of bindings) {
    if (comboMatches(b.primary, key, mods) || comboMatches(b.secondary, key, mods)) {
      return b.id;
    }
  }
  return null;
}

/**
 * Convert a mouse button number to our key string.
 * 0 = Mouse0 (left), 1 = Mouse1 (middle), 2 = Mouse2 (right)
 */
export function mouseButtonToKey(button: number): string {
  return `Mouse${button}`;
}
